import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { User, AuthTokens, LoginCredentials, RegisterData } from '../types';
import { authApi, AUTH_EXPIRED_EVENT, TOKENS_REFRESHED_EVENT, refreshTokens } from '../services/api';
import {
  setTokens,
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from '../services/tokenStorage';
import { errorMessage } from '../utils/errors';
import { connectExtension, disconnectExtension } from '../services/extensionBridge';

/**
 * Sign the browser extension in with this account. Fire and forget: the
 * extension may not be installed, and a failure must never block the dashboard.
 */
function syncExtension(user: User): void {
  connectExtension(user.id).catch(() => undefined);
}

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  /** Sign in, or create the account, with a Google ID token. */
  googleLogin: (credential: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  loadUser: () => Promise<void>;
  clearError: () => void;
  updateUser: (user: User) => void;
  forceLogout: () => void;
}

/**
 * The backend serialises users in snake_case only, but the User type also
 * carries firstName/lastName for the UI. Fill those in from the snake_case
 * fields so the two never disagree.
 */
function normalizeUser(raw: User): User {
  return {
    ...raw,
    firstName: raw.first_name ?? raw.firstName ?? '',
    lastName: raw.last_name ?? raw.lastName ?? '',
  };
}

const LOGGED_OUT: AuthState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

/**
 * The one way into a signed-in session, whichever endpoint issued it: store
 * the tokens, publish the user, then hand the account to the browser
 * extension. Password login, registration and Google sign-in all end here.
 */
async function signIn(
  request: () => Promise<{ user: User; tokens: AuthTokens }>,
  fallbackError: string
): Promise<void> {
  const set = useAuthStore.setState;
  try {
    set({ isLoading: true, error: null });
    const { user, tokens } = await request();
    setTokens(tokens);
    set({
      user: normalizeUser(user),
      tokens,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    });
    syncExtension(user);
  } catch (error) {
    set({ error: errorMessage(error, fallbackError), isLoading: false });
    throw error;
  }
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      ...LOGGED_OUT,

      login: (credentials: LoginCredentials) =>
        signIn(() => authApi.login(credentials), 'Login failed'),

      register: (data: RegisterData) =>
        signIn(() => authApi.register(data), 'Registration failed'),

      googleLogin: (credential: string) =>
        signIn(() => authApi.google(credential), 'Google sign-in failed'),

      /**
       * Revoke the refresh token server-side, then clear local state. The
       * local clear happens regardless of whether the server call succeeds
       * (the token may already be expired or blacklisted).
       */
      logout: async () => {
        // Prefer the flat key: it is the freshest after an interceptor refresh.
        const refresh = getRefreshToken() ?? get().tokens?.refresh ?? null;
        // Signing out here signs the extension out of the same account.
        disconnectExtension(get().user?.id).catch(() => undefined);
        try {
          if (refresh) {
            await authApi.logout(refresh);
          }
        } catch {
          /* clear locally regardless */
        } finally {
          clearTokens();
          set({ ...LOGGED_OUT });
        }
      },

      refreshToken: async () => {
        const tokens = await refreshTokens();
        set({ tokens });
      },

      /**
       * Re-hydrate the current user from the server on app start. If the token
       * is gone or rejected, drop to a clean logged-out state.
       */
      loadUser: async () => {
        const access = getAccessToken();
        const refresh = getRefreshToken();
        if (!access && !refresh) {
          clearTokens();
          set({ user: null, tokens: null, isAuthenticated: false });
          return;
        }
        // The flat keys are the source of truth (the interceptor writes them);
        // make sure the persisted snapshot matches before any request runs.
        if (access && refresh) {
          const current = get().tokens;
          if (!current || current.access !== access || current.refresh !== refresh) {
            set({ tokens: { access, refresh } });
          }
        }
        try {
          const user = await authApi.getProfile();
          set({ user: normalizeUser(user), isAuthenticated: true });
          syncExtension(user);
        } catch {
          clearTokens();
          set({ user: null, tokens: null, isAuthenticated: false });
        }
      },

      clearError: () => set({ error: null }),

      updateUser: (user: User) => set({ user: normalizeUser(user) }),

      // Local-only clear used when the API layer reports the session is dead.
      // Deliberately does NOT call the logout endpoint (the token is already
      // invalid, so that call would just 401).
      forceLogout: () => {
        clearTokens();
        set({ user: null, tokens: null, isAuthenticated: false });
      },
    }),
    {
      name: 'pathfindr-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
      }),
      // The axios interceptor reads the flat token keys and may have rotated
      // them since this snapshot was persisted. Only fill in flat keys that
      // are MISSING; never overwrite a fresher flat token with a stale one.
      onRehydrateStorage: () => (state) => {
        if (!state?.tokens) return;
        if (!getAccessToken() && state.tokens.access) {
          setAccessToken(state.tokens.access);
        }
        if (!getRefreshToken() && state.tokens.refresh) {
          setRefreshToken(state.tokens.refresh);
        }
      },
    }
  )
);

if (typeof window !== 'undefined') {
  // When the API layer detects an unrecoverable 401, immediately clear auth
  // state so ProtectedRoute redirects on the next render instead of after a
  // reload.
  window.addEventListener(AUTH_EXPIRED_EVENT, () => {
    useAuthStore.getState().forceLogout();
  });

  // Mirror rotated tokens into the persisted store so it never goes stale.
  window.addEventListener(TOKENS_REFRESHED_EVENT, (event) => {
    const tokens = (event as CustomEvent<AuthTokens>).detail;
    if (tokens?.access && tokens?.refresh) {
      useAuthStore.setState({ tokens });
    }
  });
}
